import { useEffect, useRef, useState } from "react";
import type { LiveGpsUpdate } from "../api";
import { masterSocketUrl } from "../api";
import { subscribeForeground } from "../lib/page-lifecycle";

interface WsMessage {
  type: string;
  deviceId?: string;
  position?: LiveGpsUpdate;
  message?: string;
}

function wsUrl(): string {
  const explicit = import.meta.env.VITE_GPS_WS_URL as string | undefined;
  return masterSocketUrl("/ws/gps", explicit);
}

export function useLiveGps(enabled = true) {
  const [positions, setPositions] = useState<Record<string, LiveGpsUpdate>>({});
  const [connected, setConnected] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let stopped = false;
    let retry: ReturnType<typeof setTimeout> | null = null;
    let delay = 1000;

    const connect = () => {
      if (stopped) return;
      const existing = wsRef.current;
      if (existing && (existing.readyState === WebSocket.OPEN || existing.readyState === WebSocket.CONNECTING)) {
        return;
      }
      const ws = new WebSocket(wsUrl());
      wsRef.current = ws;
      let ping: ReturnType<typeof setInterval> | null = null;
      ws.onopen = () => {
        delay = 1000;
        setConnected(true);
        ws.send(JSON.stringify({ action: "subscribe_all" }));
        ping = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ action: "ping" }));
        }, 25000);
      };
      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(String(ev.data)) as WsMessage;
          if (msg.type === "position" && msg.position) {
            const pos = msg.position;
            setPositions((prev) => {
              if (pos.gps_fix === false) {
                const cur = prev[pos.device_id];
                if (!cur) return prev;
                return {
                  ...prev,
                  [pos.device_id]: { ...cur, gps_fix: false, status_bits: pos.status_bits, received_at: pos.received_at },
                };
              }
              return { ...prev, [pos.device_id]: pos };
            });
          }
        } catch {
          /* ignore */
        }
      };
      ws.onerror = () => {
        setConnected(false);
      };
      ws.onclose = () => {
        if (ping) clearInterval(ping);
        setConnected(false);
        if (wsRef.current === ws) wsRef.current = null;
        if (stopped) return;
        retry = setTimeout(connect, delay);
        delay = Math.min(delay * 2, 15000);
      };
    };

    const onOnline = () => {
      delay = 1000;
      const socket = wsRef.current;
      if (socket && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)) return;
      if (retry) clearTimeout(retry);
      connect();
    };

    connect();
    window.addEventListener("online", onOnline);
    const stop = subscribeForeground(onOnline);
    return () => {
      stopped = true;
      window.removeEventListener("online", onOnline);
      stop();
      if (retry) clearTimeout(retry);
      const socket = wsRef.current;
      wsRef.current = null;
      if (socket && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)) {
        socket.close();
      }
    };
  }, [enabled]);

  return { positions, connected };
}
